<template>
  <div class="loading-dynamic-card" :class="{ 'is-done': isDone, 'is-failed': isFailed }">
    <div class="md-card-header">
      <i class="header-icon" :class="[isFailed ? 'el-icon-error' : isDone ? 'el-icon-circle-check' : 'el-icon-loading', { failed: isFailed, success: isDone }]"></i>
      <span class="header-title">{{ title || $t('workbench.loading') }}</span>
    </div>
    <div class="card-body">
      <!-- 错误提示 -->
      <div v-if="error" class="error-box">{{ $t('workbench.requestFailed', { msg: error }) }}</div>

      <!-- 状态图标和百分比 -->
      <div class="status-section">
        <div class="status-icon-wrap" :class="{ failed: isFailed, success: isDone, loading: !isDone && !isFailed }">
          <i class="status-icon" :class="isFailed ? 'el-icon-error' : isDone ? 'el-icon-circle-check' : 'el-icon-loading'"></i>
        </div>
        <div class="status-text">
          <div class="status-percent" :class="{ failed: isFailed, success: isDone, loading: !isDone && !isFailed }">
            {{ isFailed ? '—' : Math.round(progress) + '%' }}
          </div>
          <div class="status-label">{{ isFailed ? $t('workbench.flowFailed') : isDone ? $t('workbench.completed') : $t('workbench.processing') }}</div>
        </div>
      </div>

      <!-- 进度条 -->
      <div class="progress-bar-wrap">
        <div class="loading-bar-track">
          <div class="loading-bar-fill" :class="{ 'is-done': isDone, 'is-failed': isFailed }" :style="{ width: Math.round(progress) + '%' }"></div>
        </div>
      </div>

      <!-- 步骤列表 -->
      <div v-if="steps.length > 0" class="steps-section">
        <div class="steps-header">
          <div class="steps-dot" :class="{ failed: isFailed }"></div>
          <span class="steps-title">{{ $t('workbench.executionProgress') }} ({{ finishedCount + failedCount }}/{{ steps.length }})</span>
        </div>
        <div class="steps-list">
          <!-- 连接线 -->
          <div class="steps-line" :style="{ height: 'calc(100% - 12px)' }">
            <div
              class="steps-line-progress"
              :class="{ success: isDone, failed: isFailed }"
              :style="{ height: (steps.length > 1 ? ((finishedCount + failedCount) / (steps.length - 1)) * 100 : 100) + '%' }"
            ></div>
          </div>
          <!-- 步骤项 -->
          <div
            v-for="(step, index) in steps"
            :key="step.key"
            class="step-item"
            :class="{ 'is-done': step.status === 'finished', 'is-current': step.status === 'running', 'is-pending': step.status === 'ready', 'is-failed': step.status === 'failed' }"
          >
            <div
              class="step-circle"
              :class="{
                finished: step.status === 'finished',
                failed: step.status === 'failed',
                running: step.status === 'running',
                ready: step.status === 'ready',
              }"
            >
              <template v-if="step.status === 'finished'">✓</template>
              <template v-else-if="step.status === 'failed'">✕</template>
              <template v-else>{{ index + 1 }}</template>
            </div>
            <div class="step-content">
              <span class="step-name">{{ step.key }}</span>
              <span class="step-status">{{ stepLabels[step.status] || $t('workbench.stepWaiting') }}</span>
            </div>
            <i v-if="step.status === 'running'" class="el-icon-loading step-pulse"></i>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LoadingDynamic',
  props: {
    title: { type: String, default: '' },
    statusApi: { type: String, default: '' },
    stepApi: { type: String, default: '' },
    action: { type: String, default: '' },
    nextTip: { type: String, default: '' },
    getTime: { type: [Number, String], default: 1000 },
    isHistory: { type: Boolean, default: false },
    streaming: { type: Boolean, default: false }
  },
  data() {
    return {
      overallStatus: 'ready',
      steps: [],
      progress: 0,
      error: null,
      completed: false,
      pollTimer: null,
      progressTimer: null,
      estimatedTime: 0,
      startTime: 0,
      stepLabels: null
    }
  },
  computed: {
    isDone() {
      return Math.round(this.progress) >= 100
    },
    isFailed() {
      return this.overallStatus === 'failed'
    },
    finishedCount() {
      return this.steps.filter(s => s.status === 'finished').length
    },
    failedCount() {
      return this.steps.filter(s => s.status === 'failed').length
    },
    pollInterval() {
      const val = typeof this.getTime === 'string' ? parseInt(this.getTime, 10) : this.getTime
      return val || 1000
    }
  },
  created() {
    this.stepLabels = {
      finished: this.$t('workbench.stepCompleted'),
      running: this.$t('workbench.stepRunning'),
      failed: this.$t('workbench.stepFailed'),
      ready: this.$t('workbench.stepWaiting')
    }
  },
  mounted() {
    this.startPolling()
  },
  beforeDestroy() {
    this.stopPolling()
    this.stopProgressTimer()
  },
  methods: {
    startPolling() {
      const cleanStatusApi = (this.statusApi || '').replace(/\s*/g, '')
      const cleanStepApi = (this.stepApi || '').replace(/\s*/g, '')

      if (!cleanStatusApi || !cleanStepApi) {
        this.error = this.$t('workbench.noApiConfig')
        return
      }

      this.fetchData(cleanStatusApi, cleanStepApi)

      if (!this.isHistory && !this.completed) {
        this.pollTimer = setInterval(() => {
          this.fetchData(cleanStatusApi, cleanStepApi)
        }, this.pollInterval)
      }
    },
    async fetchData(statusApiUrl, stepApiUrl) {
      try {
        const [statusRes, stepRes] = await Promise.all([
          fetch(statusApiUrl, { headers: { 'Content-Type': 'application/json' }}).then(r => {
            if (!r.ok) throw new Error(`API error: ${r.status}`)
            return r.json()
          }),
          fetch(stepApiUrl, { headers: { 'Content-Type': 'application/json' }}).then(r => {
            if (!r.ok) throw new Error(`API error: ${r.status}`)
            return r.json()
          })
        ])

        const newStatus = statusRes.data.status
        const estimatedTime = statusRes.data.estimatedTime
        this.overallStatus = newStatus

        // 首次成功请求时启动进度计时器
        if (!this.isHistory && !this.startTime) {
          this.startProgressTimer(estimatedTime && estimatedTime > 0 ? estimatedTime : 60)
        }

        const stepData = stepRes.data
        const stepList = Object.entries(stepData).map(([key, value]) => ({
          key,
          status: value
        }))
        this.steps = stepList

        const shouldStopPolling = newStatus === 'finished' || newStatus === 'failed'
        const allDone = stepList.every(s => s.status === 'finished' || s.status === 'failed')

        this.error = null

        if ((allDone || shouldStopPolling) && !this.completed) {
          this.completed = true
          this.stopProgressTimer()
          this.progress = 100
          this.stopPolling()

          if (this.action && !this.isHistory) {
            const message = this.nextTip || 'loading_complete'
            this.sendSilentMessage(`[card-action]${message}`)
          }
        } else if (!this.startTime) {
          // 没有基于预估时间的计时器，使用步骤完成比例
          const total = stepList.length
          const finishedCount = stepList.filter(s => s.status === 'finished').length
          const failedCount = stepList.filter(s => s.status === 'failed').length
          const stepSlice = 100 / total
          const baseProgress = finishedCount * stepSlice + failedCount * stepSlice
          this.progress = Math.min(baseProgress, 99.9)
        }
      } catch (err) {
        this.error = err.message || this.$t('workbench.apiFailed')
      }
    },
    startProgressTimer(estimatedTime) {
      this.stopProgressTimer()
      this.estimatedTime = estimatedTime
      this.startTime = Date.now()

      this.progressTimer = setInterval(() => {
        if (this.completed) return
        const elapsed = Date.now() - this.startTime
        const estimatedMs = this.estimatedTime * 1000
        if (elapsed >= estimatedMs) {
          this.progress = 98
        } else {
          this.progress = (elapsed / estimatedMs) * 98
        }
      }, 200)
    },
    stopProgressTimer() {
      if (this.progressTimer) {
        clearInterval(this.progressTimer)
        this.progressTimer = null
      }
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    sendSilentMessage(message) {
      // 通过自定义事件发送消息
      document.dispatchEvent(new CustomEvent('md-card-action', {
        detail: { action: this.action, value: message, message }
      }))
    }
  }
}
</script>

<style lang="scss" scoped>
.loading-dynamic-card {
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border: 1px solid #3370ff;
  max-width: 100%;
  width: 100%;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  overflow: hidden;

  &.is-failed {
    border-color: #ef4444;
  }
  &.is-done {
    border-color: transparent;
  }
}

.md-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-bottom: 1px solid #f0f0f0;
}

.header-icon {
  font-size: 16px;
  color: #3370ff;
  &.failed { color: #ef4444; }
  &.success { color: #3370ff; }
}

.header-title {
  font-size: 14px;
  font-weight: 500;
  color: #1f2329;
}

.card-body {
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.error-box {
  width: 100%;
  margin-bottom: 16px;
  padding: 12px;
  border-radius: 8px;
  background: #fef2f2;
  color: #ef4444;
  font-size: 12px;
}

.status-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
}

.status-icon-wrap {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  transition: all 0.3s;

  &.failed {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    box-shadow: 0 4px 15px rgba(239, 68, 68, 0.4);
  }
  &.success {
    background: linear-gradient(135deg, #4A5FF3 0%, #3A4FD3 100%);
    box-shadow: 0 4px 15px rgba(74, 95, 243, 0.4);
  }
  &.loading {
    background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
    box-shadow: 0 4px 15px rgba(24, 144, 255, 0.4);
  }
}

.status-icon {
  font-size: 36px;
  color: #fff;
}

.status-text {
  text-align: center;
}

.status-percent {
  font-size: 30px;
  font-weight: 700;
  &.failed { color: #ef4444; }
  &.success { color: #3370ff; }
  &.loading { color: #1890ff; }
}

.status-label {
  margin-top: 4px;
  font-size: 12px;
  color: #9ca3af;
}

.progress-bar-wrap {
  width: 100%;
  margin-bottom: 24px;

  .loading-bar-track {
    width: 100%;
    height: 10px;
    background: #f0f0f0;
    border-radius: 5px;
    overflow: hidden;
  }

  .loading-bar-fill {
    height: 100%;
    background: linear-gradient(90deg, #1890ff, #096dd9);
    border-radius: 5px;
    transition: width 0.2s linear;
    &.is-done {
      background: linear-gradient(90deg, #4A5FF3, #3A4FD3);
    }
    &.is-failed {
      background: linear-gradient(90deg, #ef4444, #dc2626);
    }
  }
}

.steps-section {
  width: 100%;
  border-radius: 12px;
  padding: 20px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.steps-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.steps-dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #3370ff;
  &.failed { background: #ef4444; }
}

.steps-title {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
}

.steps-list {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.steps-line {
  position: absolute;
  top: 12px;
  left: 11px;
  width: 2px;
  background: #e5e7eb;
  z-index: 0;
}

.steps-line-progress {
  width: 100%;
  transition: height 0.5s;
  max-height: 100%;
  background: linear-gradient(to bottom, #1890ff, #096dd9);
  &.success { background: linear-gradient(to bottom, #4A5FF3, #3A4FD3); }
  &.failed { background: linear-gradient(to bottom, #ef4444, #dc2626); }
}

.step-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  z-index: 1;
  transition: all 0.3s;
  &.pending { opacity: 0.6; }
}

.step-circle {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
  background: #F7F8FC;
  color: #9ca3af;
  border: 1px solid #E5E7EB;
  transition: all 0.3s;

  &.finished {
    background: linear-gradient(135deg, #4A5FF3 0%, #3A4FD3 100%);
    color: #fff;
    border: none;
    box-shadow: 0 0 8px rgba(74, 95, 243, 0.4);
  }
  &.failed {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: #fff;
    border: none;
    box-shadow: 0 0 8px rgba(239, 68, 68, 0.4);
  }
  &.running {
    background: #AAB5FF;
    color: #fff;
    border: 2px solid #AAB5FF;
  }
  &.ready {
    background: #F7F8FC;
    color: #9ca3af;
    border: 1px solid #E5E7EB;
  }
}

.step-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.step-name {
  font-size: 12px;
  font-weight: 500;
  transition: color 0.3s;
  color: #6b7280;
  .step-item.is-done & { color: #3A4FD3; }
  .step-item.is-current & { color: #AAB5FF; }
  .step-item.is-failed & { color: #dc2626; }
}

.step-status {
  font-size: 11px;
  margin-top: 2px;
  color: #9ca3af;
  .step-item.is-done & { color: #3370ff; }
  .step-item.is-current & { color: #AAB5FF; }
  .step-item.is-failed & { color: #ef4444; }
}

.step-pulse {
  color: #3370ff;
  font-size: 16px;
  flex-shrink: 0;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
